<template>
	<div class="w-full p-2 md:p-8 flex items-center">
		<slot
			:logout="logout"
			:login="login"
			:email="email"
			:password="password"
			:createAccount="createAccount"
			:sendRecoveryEmail="sendRecoveryEmail"
			:resetPassword="resetPassword"
		>
			<form @submit.stop.prevent class="rounded-lg flex flex-col space-y-4 w-full">
				<template id="creatingAccountForm" v-if="creatingAccount">
					<div class="flex w-full justify-center items-center md:pt-0">
						<div class="w-full max-w-3xl">
							<h1 class="flex w-full justify-center text-xl md:text-3xl font-semibold text-ihh-midnight-gray pb-10">New Account</h1>
							<div class="flex w-full justify-center gap-x-4">
								<div class="grid grid-cols-1 sm:grid-cols-2 place-items-center w-full lg:max-w-5xl gap-x-8">
									<!-- Email -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="email">Email Address</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<input
												v-model="user.email.value"
												id="email"
												type="email"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('email') }"
											/>
										</div>
									</div>

									<!-- Password -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="password1">Password</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<input v-model="user.password1.value" id="password1" type="password" class="pl-1 w-full h-12 text-gray-400 font-normal" />
										</div>
									</div>

									<!-- Confirm Password -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="password2">Confirm Password</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<input v-model="user.password2.value" id="password2" type="password" class="pl-1 w-full h-12 text-gray-400 font-normal" />
										</div>
									</div>

									<!-- First Name -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="firstName">First Name</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<input
												v-model="user.firstName.value"
												id="firstName"
												type="text"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('firstName') }"
											/>
										</div>
									</div>

									<!-- Last Name -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="lastName">Last Name</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<input
												v-model="user.lastName.value"
												id="lastName"
												type="text"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('lastName') }"
											/>
										</div>
									</div>

									<!-- Company -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="company">Company Name</label>
										</div>
										<div class="h-10 mb-3">
											<input
												v-model="user.company.value"
												id="company"
												type="text"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('company') }"
											/>
										</div>
									</div>

									<!-- Phone -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="phone">Phone Number</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<input
												:value="formattedPhone"
												@input="formatPhoneNumber"
												id="phone"
												type="tel"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('phone') }"
											/>
										</div>
									</div>

									<!-- Street Address -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="street">Street Address</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<input
												v-model="user.street.value"
												id="street"
												type="text"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('street') }"
											/>
										</div>
									</div>

									<!-- Apt/Ste/Rm -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="apt">Apt/Ste/Rm</label>
										</div>
										<div class="h-10 mb-3">
											<input
												v-model="user.apt.value"
												id="apt"
												type="text"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('apt') }"
											/>
										</div>
									</div>

									<!-- City -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="city">{{ isMilitaryAddress ? 'Military City' : 'City' }}</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<select
												v-if="isMilitaryAddress"
												v-model="user.city.value"
												id="city"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('city') }"
											>
												<option value="" disabled>Select military city</option>
												<option v-for="city in militaryCities" :key="city.code" :value="city.code">{{ city.code }} - {{ city.name }}</option>
											</select>
											<input
												v-else
												v-model="user.city.value"
												id="city"
												type="text"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('city') }"
											/>
										</div>
									</div>

									<!-- Country -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="country">Country</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<select
												v-model="user.country.value"
												@change="fetchStates"
												id="country"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('country') }"
											>
												<option value="" disabled>Select Country</option>
												<option v-for="country in countries" :key="country.isoCode" :value="country.isoCode">{{ country.name }}</option>
											</select>
										</div>
									</div>

									<!-- State -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="state">{{ isMilitaryAddress ? 'Military State' : 'State' }}</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<select
												v-model="user.state.value"
												id="state"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('state') }"
											>
												<option disabled value="">{{ isMilitaryAddress ? 'Select military state' : 'Select State' }}</option>
												<template v-if="isMilitaryAddress">
													<option v-for="state in militaryStates" :key="state.code" :value="state.code">{{ state.code }} - {{ state.name }}</option>
												</template>
												<template v-else>
													<option v-for="state in states" :key="state.name" :value="state.name">{{ state.name }}</option>
												</template>
											</select>
										</div>
									</div>

									<!-- Zip Code -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<div class="flex justify-between">
											<label class="font-semibold text-sm" for="zip">Zip Code</label>
											<span class="font-semibold uppercase text-sm">Required</span>
										</div>
										<div class="h-10 mb-3">
											<input
												v-model="user.zip.value"
												id="zip"
												type="tel"
												class="w-full h-12 font-normal pl-1 transition-all duration-300"
												:class="{ '!ring-2 !ring-ihh-salmon !border-ihh-salmon': highlightedFields.includes('zip') }"
											/>
										</div>
									</div>

									<!-- Baby Due Date -->
									<div class="w-full text-ihh-gray-700 font-medium">
										<label class="font-semibold text-sm" for="dueDate">Baby Due Date</label>
										<div class="h-10 mb-3">
											<input v-model="user.dueDate.value" id="dueDate" type="date" class="pl-1 w-full h-12 text-gray-400 font-normal" />
										</div>
									</div>
								</div>
							</div>

							<!-- Questions Section -->
							<section class="px-4 py-1 bg-gray-100 rounded mt-4">
								<!-- SMS Consent -->
								<div class="m-auto my-3 items-center flex w-full">
									<div class="flex items-center m-auto w-full pr-4">
										<label
											class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2"
										>
											<input type="checkbox" v-model="user.smsConsent.value" class="absolute opacity-0 w-0 h-0" />
											<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
										</label>
										<div class="text-ihh-slate-gray text-sm items-center text-start">Subscribe to marketing texts</div>
									</div>
								</div>

								<!-- Healthcare Provider -->
								<div class="m-auto my-3 items-center flex w-full">
									<div class="flex items-center m-auto w-full pr-4">
										<label
											class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2"
										>
											<input type="checkbox" v-model="user.hcProvider.value" class="absolute opacity-0 w-0 h-0" />
											<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
										</label>
										<div class="text-ihh-slate-gray text-sm items-center text-start">Are you a healthcare provider?</div>
									</div>
								</div>

								<!-- Military Address Toggle -->
								<div class="m-auto my-3 items-center flex w-full">
									<div class="flex items-center m-auto w-full pr-4">
										<label
											class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2"
										>
											<input type="checkbox" v-model="isMilitaryAddress" class="absolute opacity-0 w-0 h-0" />
											<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
										</label>
										<div class="text-ihh-slate-gray text-sm items-center text-start">Shipping to a US Military Address (APO/FPO/DPO)</div>
									</div>
								</div>
							</section>
						</div>
					</div>

					<div>
						<div class="flex justify-center">
							<!-- change button color -->
							<button
								@click.stop.prevent="action"
								class="w-full xs:w-fit text-sm md:text-base font-semibold text-ihh-midnight-gray py-3 px-10 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide"
							>
								<template v-if="!processing">{{ buttonText }}</template>
								<Spinner v-else class="border-t-family-at-church-orange w-4 h-4 my-1" :small="true" color="border-t-white/50" />
							</button>
						</div>

						<button v-if="store.user?.id" @click="logout" class="text-xs text-gray-400 hover:underline">Logout</button>

						<div class="text-center select-none mt-2">
							<!-- <button v-if="!creatingAccount"     @click="creatingAccount = true; guest = false; forgotPassword = false" class="text-xs text-gray-400 hover:underline">Create Account</button> -->
							<button
								@click.stop.prevent="toLogin"
								class="text-xs text-gray-400 hover:underline"
								:class="{ 'ml-2 pl-2 border-l dark:border-gray-500': resettingPassword }"
							>
								{{ resettingPassword || (allowGuest && !guest) ? 'Login' : 'Have an account? Login instead.' }}
							</button>
							<!-- <button v-if="allowGuest && !guest" @click="creatingAccount = true; guest = true; forgotPassword = false" class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500">Continue as Guest</button> -->
							<!-- <button v-if="!creatingAccount && !resettingPassword"     @click="creatingAccount = false; guest = false; forgotPassword = true" class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500">Forgot Password?</button> -->
						</div>
					</div>
				</template>

				<!-- <template v-else-if="forgotPassword">
                    <div class="w-full">
                        <div class="w-7/8">
                            <div class="flex justify-center">
                                <div class="flex flex-col w-1/2 mb-8 p-4 rounded-lg shadow-lg bg-gradient-to-b from-ihh-pink to-ihh-purple pb-8">
                                    <h3 class="flex justify-center text-xl md:text-2xl font-semibold tracking-wide text-ihh-midnight-gray pt-8">Recover Password</h3>
                                    <div class="w-full">
                                        <div class="my-6 md:max-w-md mx-auto">
                                            <div class="text-sm text-ihh-gray-600 mb-1">Email Address:</div>
                                            <input
                                                v-model="email"
                                                type="text"
                                                required
                                                class="w-96 py-3 pl-4 flex border border-ihh-gray-600 rounded text-sm md:text-base bg-white font-light text-black placeholder-ihh-gray relative"
                                            />
                                        </div>
                                        <div v-if="!creatingAccount" class="my-6 md:max-w-md mx-auto ">
                                            <div class="flex justify-center">
                                                <button @click="action" class="w-48 px-3 py-2 bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide dark:text-white/75 text-white font-normal rounded flex justify-center">
                                                    <template v-if="!processing">{{ buttonText }}</template>
                                                    <Spinner v-else class="border-t-family-at-church-orange w-4 h-4 my-1" :small="true" color="border-t-white/50" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template> -->

				<template v-else>
					<div class="w-full">
						<!-- beginning of the sign-in/login that I created -->

						<div class="flex w-full justify-center text-xl md:text-3xl font-semibold tracking-wide text-ihh-midnight-gray pb-5">
							{{ forgotPassword ? 'Recover Password' : resettingPassword ? 'Update Password' : 'Sign In' }}
						</div>
						<div class="flex w-full justify-center">
							<div class="w-full max-w-4xl">
								<div class="flex w-full" :class="{ 'grid grid-cols-1 md:grid-cols-2 gap-x-6 justify-around': !small }">
									<div class="flex w-full justify-center items-center text-4xl font-semibold">
										<div class="flex w-full justify-center gap-y-6 flex-col items-center my-8 px-6 md:px-0">
											<div class="relative w-full md:max-w-md" v-if="!resettingPassword">
												<div class="text-sm text-ihh-gray-600 mb-2">Email Address:</div>
												<input
													v-model="email"
													type="text"
													required
													class="py-3 pl-4 flex border border-ihh-gray-600 rounded w-full text-sm md:text-base bg-white font-light text-black placeholder-ihh-gray relative"
												/>
											</div>
											<div class="relative w-full md:max-w-md" v-if="!forgotPassword">
												<div class="text-sm text-ihh-gray-600 mb-2">Password:</div>
												<input
													v-model="password"
													type="password"
													required
													class="py-3 pl-4 flex border border-ihh-gray-600 rounded w-full text-sm md:text-base bg-white font-light text-black placeholder-ihh-gray relative"
												/>
											</div>

											<div v-if="!creatingAccount" class="my-4 md:max-w-md">
												<div class="flex justify-center">
													<button
														@click.stop.prevent="action"
														class="w-full xs:w-fit text-sm md:text-base font-semibold text-ihh-midnight-gray py-3 px-10 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide"
													>
														<template v-if="!processing">{{ buttonText }}</template>
														<Spinner v-else class="border-t-family-at-church-orange w-4 h-4 my-1" :small="true" color="border-t-white/50" />
													</button>
												</div>

												<button v-if="store.user?.id" @click="logout" class="text-xs text-gray-400 hover:underline">Logout</button>

												<div class="text-center select-none">
													<button v-if="!creatingAccount" @click="handleCreateAccount" class="text-xs text-gray-400 hover:underline">
														Create Account
													</button>
													<button
														v-if="creatingAccount || resettingPassword"
														@click="toLogin"
														class="text-xs text-gray-400 hover:underline"
														:class="{ 'ml-2 pl-2 border-l dark:border-gray-500': resettingPassword }"
													>
														{{ resettingPassword || (allowGuest && !guest) ? 'Login' : 'Have an account? Login instead.' }}
													</button>
													<button
														v-if="allowGuest && !guest"
														@click="handleGuestContinue"
														class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500"
													>
														Continue as Guest
													</button>
													<button
														v-if="!creatingAccount && !resettingPassword && !forgotPassword"
														@click="toForgotPassword"
														class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500"
													>
														Forgot Password?
													</button>
													<button
														v-if="forgotPassword"
														@click="forgotPassword = false"
														class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500"
													>
														Return to Login
													</button>
												</div>
											</div>
										</div>
									</div>

									<div v-if="!small" class="flex flex-col items-center md:block">
										<div class="p-6 md:p-12 w-full justify-end bg-ihh-light-lavendar rounded-xl shadow-md max-w-sm md:max-w-md">
											<div class="text-xl font-semibold tracking-wide text-ihh-midnight-gray">New Here?</div>
											<div class="py-4">
												<div class="text-base text-ihh-gray-600">Create an account with us and you'll be able to:</div>
												<ul class="text-ihh-gray-600 list-disc ml-12 font-normal">
													<li class="text-normal font-thin">Check out faster</li>
													<li class="text-normal font-thin">Save multiple shipping addresses</li>
													<li class="text-normal font-thin">Access your order history</li>
													<li class="text-normal font-thin">Track new orders</li>
													<li class="text-normal font-thin">Save items to your Wish List</li>
												</ul>
												<div class="mt-4 w-full flex justify-center">
													<button
														@click="handleCreateAccount"
														class="flex md:w-fit text-center justify-center md:justify-start text-sm md:text-base font-semibold text-ihh-midnight-gray py-3 px-10 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide"
													>
														Create Account
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div class="w-full flex flex-col items-center md:items-start mt-8 md:mt-0 mb-24">
									<div v-if="!small" class="flex max-w-sm md:max-w-lg md:w-1/2 justify-center">
										<div class="p-10 bg-ihh-light-lavendar w-full rounded-xl shadow-md md:max-w-md">
											<div class="text-xl font-semibold tracking-wide text-ihh-midnight-gray">Are you a Midwife?</div>
											<div class="py-4">
												<div class="text-base text-ihh-gray-600">Setup your birth kit with us:</div>
												<ul class="text-ihh-gray-600 list-disc ml-12 font-normal">
													<li class="text-normal font-thin">Wide array of birth items to choose from</li>
													<li class="text-normal font-thin">Fully customizable</li>
													<li class="text-normal font-thin">Personalized page on our website</li>
													<li class="text-normal font-thin">Birth kits shipped directly to your clients home</li>
												</ul>
												<div class="mt-4 w-full flex justify-center">
													<button
														class="flex md:w-fit text-center justify-center md:justify-start text-sm md:text-base font-semibold text-ihh-midnight-gray py-3 px-10 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide"
													>
														<router-link to="/new-birth-kit-setup">Setup custom birth kit</router-link>
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- end of the sign-in/login that I created -->
					</div>
				</template>
			</form>
		</slot>
	</div>
</template>

<script>
import axios from 'axios'
import { Country, State } from 'country-state-city'
import Swal from 'sweetalert2'
import { validateEmail } from '../../helpers/email'
import { getUser, accountCreateErrorMessage } from '../../helpers/user'
import { useCheckoutStore } from '../../stores/store.js'
import Mango from '../../helpers/mango'
import { user } from '../../helpers/newUser'
import aa from 'search-insights'
import { loadGoogleMapsScript, isGoogleMapsReady } from '../../helpers/googleMaps.js'
import { logoutSession } from '../../helpers/session.js'
import { sessionStoreErrorMessage } from '../../helpers/browserSession.js'

export default {
	props: ['small'],
	inject: ['store', 'axios'],
	emits: ['loggedIn', 'hide'],
	data() {
		return {
			checkout: useCheckoutStore(),
			email: null,
			password: null,
			user,
			countries: [],
			states: [],
			processing: false,
			creatingAccount: false,
			guest: false,
			forgotPassword: false,
			passwordMatchError: null,
			passwordComplexityError: null,
			googleMapsLoaded: false,
			highlightedFields: [],
			formattedPhone: '',
			autocompleteInstance: null,
			isMilitaryAddress: false,
			militaryCities: [
				{ code: 'APO', name: 'Army Post Office' },
				{ code: 'FPO', name: 'Fleet Post Office' },
				{ code: 'DPO', name: 'Diplomatic Post Office' },
			],
			militaryStates: [
				{ code: 'AA', name: 'Armed Forces Americas' },
				{ code: 'AE', name: 'Armed Forces Europe' },
				{ code: 'AP', name: 'Armed Forces Pacific' },
			],
		}
	},
	methods: {
		toLogin() {
			this.creatingAccount = false
			this.guest = false
			this.forgotPassword = false
			this.$router.push('/login')
		},
		toForgotPassword() {
			this.creatingAccount = false
			this.guest = false
			this.forgotPassword = true
		},
		handleCreateAccount() {
			this.creatingAccount = true
			this.guest = false
			this.forgotPassword = false
			this.$router.push('/register')
		},
		handleGuestContinue() {
			this.creatingAccount = true
			this.guest = true
			this.forgotPassword = false
			this.$router.push('/register')
		},
		validateEmail,
		fetchCountries() {
			this.countries = Country.getAllCountries()
		},
		fetchStates() {
			if (this.user.country.value) {
				this.states = State.getStatesOfCountry(this.user.country.value)
			}
		},
		initAutoComplete() {
			if (this.isMilitaryAddress) return

			this.$nextTick(() => {
				const streetInput = document.getElementById('street')
				if (!streetInput || !isGoogleMapsReady()) return

				this.autocompleteInstance = new google.maps.places.Autocomplete(streetInput, {
					types: ['address'],
					componentRestrictions: { country: 'us' },
					fields: ['address_components'],
				})

				this.autocompleteInstance.addListener('place_changed', () => {
					const place = this.autocompleteInstance.getPlace()
					if (!place || !place.address_components) return

					const addressComponents = {}

					for (const component of place.address_components) {
						const types = component.types

						if (types.includes('street_number')) {
							addressComponents.street_number = component.long_name
						} else if (types.includes('route')) {
							addressComponents.route = component.long_name
						} else if (types.includes('locality')) {
							addressComponents.city = component.long_name
						} else if (types.includes('administrative_area_level_1')) {
							addressComponents.state = component.long_name
							addressComponents.stateCode = component.short_name
						} else if (types.includes('country')) {
							addressComponents.country = component.short_name
						} else if (types.includes('postal_code')) {
							addressComponents.postal_code = component.long_name
						}
					}

					// Update the form fields
					this.user.street.value = `${addressComponents.street_number || ''} ${addressComponents.route || ''}`.trim()
					this.user.city.value = addressComponents.city || ''
					this.user.zip.value = addressComponents.postal_code || ''

					// Update country and fetch states
					if (addressComponents.country) {
						this.user.country.value = addressComponents.country
						this.fetchStates()
					}

					// Set state after states are loaded
					this.$nextTick(() => {
						if (addressComponents.state) {
							this.user.state.value = addressComponents.state
						}

						// Highlight the auto-filled fields
						this.applyFieldHighlight(['street', 'city', 'zip', 'country', 'state'])
					})
				})
			})
		},
		applyFieldHighlight(fieldIds) {
			this.highlightedFields = [...fieldIds]
			setTimeout(() => {
				this.highlightedFields = []
			}, 800)
		},
		formatPhoneNumber(event) {
			// Only allow numerical digits
			const input = event.target.value.replace(/\D/g, '')

			// Limit to 10 digits
			const limited = input.slice(0, 10)

			// Store the raw digits in the user.phone.value property
			this.user.phone.value = limited

			// Format as (xxx) xxx-xxxx for display
			let formatted = ''
			if (limited.length === 0) {
				formatted = ''
			} else if (limited.length <= 3) {
				formatted = `(${limited}`
			} else if (limited.length <= 6) {
				formatted = `(${limited.slice(0, 3)}) ${limited.slice(3)}`
			} else {
				formatted = `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`
			}

			this.formattedPhone = formatted
		},
		validatePasswordComplexity(password) {
			const minLength = 7
			const hasAlphabetic = /[a-zA-Z]/.test(password)
			const hasNumeric = /[0-9]/.test(password)

			if (password.length < minLength) {
				this.passwordComplexityError = 'Password must be at least 7 characters long.'
			} else if (!hasAlphabetic) {
				this.passwordComplexityError = 'Password must contain at least one alphabetic character.'
			} else if (!hasNumeric) {
				this.passwordComplexityError = 'Password must contain at least one numeric character.'
			} else {
				this.passwordComplexityError = null
			}
		},
		validatePasswordsMatch() {
			if (this.user.password1.value !== this.user.password2.value) {
				this.passwordMatchError = 'Passwords do not match'
			} else {
				this.passwordMatchError = null
			}
		},
		validateForm() {
			const requiredFields = ['email', 'password1', 'password2', 'firstName', 'lastName', 'street', 'city', 'state', 'zip', 'country', 'phone']

			for (const field of requiredFields) {
				if (!this.user[field].value) {
					return Swal.fire({
						icon: 'error',
						title: `${this.user[field].display} is required`,
						text: `Please fill out the ${this.user[field].display} field`,
					})
				}
			}

			this.validatePasswordsMatch()
			this.validatePasswordComplexity(this.user.password1.value)

			if (this.passwordMatchError) {
				Swal.fire({
					icon: 'error',
					title: 'Passwords do not match',
					text: 'Please make sure your passwords match',
				})
				return { success: false }
			} else if (this.passwordComplexityError) {
				Swal.fire({
					icon: 'error',
					title: 'Password complexity error',
					text: this.passwordComplexityError,
				})
				return { success: false }
			}

			return { success: true }
		},
		async login(email, password) {
			email = this.email || email
			password = this.password || password

			console.log('email typeof: ', typeof email)
			console.log('email: ', email)
			// Check if email is an event object (MouseEvent, PointerEvent, etc.)
			// This happens when the login method is called directly from a button click
			if (email instanceof Event || (typeof email === 'object' && email.type === 'click')) {
				email = this.email
				password = this.password
			}

			const onCheckout =
				this.$route.path === '/checkout' || (typeof this.$route.path === 'string' && this.$route.path.startsWith('/checkout'))

			let member = {}
			this.processing = true

			if (onCheckout) {
				this.checkout.updateLoading(true)
			}

			try {
				try {
					member = await Mango.login({ email, password })
				} catch (error) {
					console.log('Error: ', error)
				}

				if (member?.success && member?.memberId) {
					if (member.roles?.includes('admin')) member.admin = true

					// Clear stale nav login redirect before initUserCart sets store.user (nav watches loggedIn)
					if (onCheckout) {
						this.store.login = null
					}

					// Mango.login stored the cookie session and did not keep the bearer token.
					// Set Algolia user token
					aa('setAuthenticatedUserToken', member.memberId)

					if (onCheckout) {
						this.checkout.resetCheckoutForLoginPreserveProgress()
					} else {
						this.checkout.resetCheckoutForLogout()
					}

					// const user = await getUser();
					// this.store.user = user;

					// Merge recenlty viewed items
					if (this.store?.recentlyViewed?.length) {
						let items = this.store.user.recentlyViewed || []
						items = items.concat(this.store.recentlyViewed)
						if (items.length > 10) {
							items = items.slice(items.length - 10)
						}
						// Never let this block the login: store.user is still the guest here, so its id can be empty and the save
						// is rejected ("email is required"). A failure here used to stop the login before the cart merge ran.
						try {
							await Mango.members.save({ id: this.store.user?.id || member.memberId, recentlyViewed: items })
							this.store.recentlyViewed = []
						} catch (error) {
							console.log('Could not save recently viewed after login: ', error)
						}
						// store.user will be set to the Mango user again in initUserCart() below.
						// This will update the store.user.recentlyViewed
					}

					// Fold the guest cart into the member's cart on the server (POST /controllers/cart/merge). The server
					// keeps every line from both carts, re-syncs BigCommerce for the customer, and clears the guest cookie.
					await this.store.mergeGuestCart?.()

					// Note, this initUserCart() is resettting the store user to the Mango user (needs to happen after
					// merging recently viewed above) AND it is reloading the merged cart from the server (required to
					// pass the store.user into useCart again)
					await this.store.initUserCart()

					// Handle redirection and completion
					this.$emit('loggedIn')
					this.$emit('hide') // FIXED the issue with login modal not closing after successful login by adding this

					console.log('this.$route.path: ', this.$route.path)

					if (onCheckout) {
						window.dispatchEvent(new CustomEvent('checkout-session-ready'))
						return
					}

					if (this.small) {
						return
					}

					console.log('Redirecting to Home')
					let restrictedPath = this.store?.login?.next || '/'
					if (restrictedPath) this.$router.push(restrictedPath)
				} else if (member?.invalidFields || !member?.success) {
					console.log('member.Id not returned')
					console.log(member)
					const unavailable = sessionStoreErrorMessage(member)
					Swal.fire({
						title: unavailable || `Invalid ${(member?.invalidFields || []).join(', ')}`,
						icon: 'error',
					})
				}
			} catch (error) {
				console.error('Error authenticating to Mango', error)
			} finally {
				if (onCheckout && !member?.success) {
					this.checkout.updateLoading(false)
				}
			}

			this.processing = false
		},
		async logout() {
			this.store.showBag = false
			window.localStorage.removeItem('recentlyViewed')
			await logoutSession({ store: this.store, axios: this.axios, checkout: this.checkout })
			this.$emit('loggedOut')
			this.$emit('hide')

			if (this.$route.path === '/checkout') {
				window.location.reload()
			} else {
				this.$router.push('/login')
			}
		},
		async createAccount() {
			if (!this.validateForm().success) return

			const militaryStateMap = {
				AA: 'Armed Forces Americas',
				AE: 'Armed Forces Europe',
				AP: 'Armed Forces Pacific',
			}

			let stateValue = this.user.state.value
			if (this.isMilitaryAddress && militaryStateMap[stateValue]) {
				stateValue = militaryStateMap[stateValue]
			}

			const data = {
				email: this.user.email.value,
				firstName: this.user.firstName.value,
				lastName: this.user.lastName.value,
				company: this.user.company.value,
				phone: this.user.phone.value,
				address1: this.user.street.value,
				address2: this.user.apt.value,
				city: this.user.city.value,
				country: this.user.country.value,
				state: stateValue,
				zip: this.user.zip.value,
				password: this.user.password1.value,
				smsConsent: this.user.smsConsent.value,
				dueDate: this.user.dueDate.value,
				hcProvider: this.user.hcProvider.value,
				isMilitary: this.isMilitaryAddress,
			}

			try {
				let response = await axios.post(`${this.store.api}/controllers/account/create`, data)

				console.log(response.data.id)

				if (!response.data.id) {
					return Swal.fire({
						icon: 'error',
						title: 'Error creating account',
						text: accountCreateErrorMessage(response.data),
					})
				} else if (response.data.id) {
					let member = await Mango.login({ email: data.email, password: data.password })

					if (member?.memberId) {
						if (member.roles?.includes('admin')) member.admin = true

						const user = await getUser()
						this.store.user = user

						// Handle redirection and completion
						this.$emit('loggedIn')
						this.$emit('hide') // FIXED the issue with login modal not closing after successful login by adding this

						let restrictedPath = this.store?.login?.next || '/'
						if (restrictedPath) this.$router.push(restrictedPath)
					} else if (member?.invalidFields || member?.code === 'session_store_unavailable') {
						Swal.fire({ title: sessionStoreErrorMessage(member) || `Invalid ${(member.invalidFields || []).join(', ')}`, icon: 'error' })
					}

					this.$emit('accountCreated')
					this.$emit('hide')

					const redirectEmail = this.user.email.value
					this.resetAccountForm()
					this.$router.push({ path: '/welcome', query: { email: redirectEmail } })
					this.processing = false
				}
			} catch (error) {
				console.error('Error creating account', error)
				return Swal.fire({
					icon: 'error',
					title: 'Error creating account',
					text: accountCreateErrorMessage(error),
				})
			}
		},
		resetAccountForm() {
			this.user.email.value = ''
			this.user.password1.value = ''
			this.user.password2.value = ''
			this.user.firstName.value = ''
			this.user.lastName.value = ''
			this.user.company.value = ''
			this.user.street.value = ''
			this.user.apt.value = ''
			this.user.city.value = ''
			this.user.country.value = 'US'
			this.user.state.value = 'Texas'
			this.user.zip.value = ''
			this.user.phone.value = ''
			this.user.dueDate.value = ''
			this.user.smsConsent.value = true
			this.user.hcProvider.value = false
			this.formattedPhone = ''
			this.isMilitaryAddress = false
		},
		async sendRecoveryEmail() {
			let email = this.email
			this.processing = true
			let response = await this.axios.post(`${this.store.api}/controllers/account/sendResetInstructions`, { email, forgot: true })
			if (response.data.success) {
				// Swal.fire('Success!', `Recovery instructions sent to: ${this.email}<br><br>If you don't receive an email within a couple minutes, please check your junk folder.`, 'success')
				Swal.fire('Success!', `Recovery instructions sent to: ${this.email}`, 'success')
				this.forgotPassword = false
			} else {
				Swal.fire('Invalid Email!', `The following user does not exist: ${this.email}`, 'warning')
			}
			this.$emit('emailSent')
			this.$emit('hide')
			this.processing = false
		},
		async resetPassword() {
			this.validatePasswordComplexity(this.password)
			if (this.passwordComplexityError) {
				return Swal.fire({
					icon: 'error',
					title: 'Password Complexity Error',
					text: this.passwordComplexityError,
				})
			}
			this.processing = true

			let data = {
				email: this.$route.query.email,
				salt: this.$route.query.salt,
				password: this.password,
			}

			let response = await this.axios.post(`${this.store.api}/controllers/account/resetPassword`, data)
			console.log(response.data)
			if (response.data.success) {
				this.email = this.$route.query.email
				console.log('Logging in after password reset')
				await this.login()
				this.$router.push('/account')
				Swal.fire('Success!', 'Your password has been reset.', 'success')
			} else {
				Swal.fire('Invalid Link!', `The link you're using is invalid.`, 'error')
			}

			this.processing = false
			this.$emit('passwordReset')
			this.$emit('hide')
		},
	},
	computed: {
		guestPassword() {
			return Math.random().toString(36).substr(2) + Math.random().toString(36).substr(2)
		},
		labelText() {
			return this.resettingPassword
				? 'Reset Password'
				: this.forgotPassword
					? 'Recover Password'
					: this.creatingAccount
						? this.guest
							? 'Continue as Guest'
							: 'Create Account'
						: 'Login'
		},
		buttonText() {
			return this.resettingPassword
				? 'Update Password'
				: this.forgotPassword
					? 'Send Recovery Email'
					: this.creatingAccount
						? this.guest
							? 'Continue'
							: 'Create Account'
						: 'Login'
		},
		action() {
			return this.resettingPassword ? this.resetPassword : this.forgotPassword ? this.sendRecoveryEmail : this.creatingAccount ? this.createAccount : this.login
		},
		resettingPassword() {
			return !!this.$route.query?.salt
		},
		loggedIn() {
			return !!this.store.user?.id
		},
	},
	created() {
		this.fetchCountries()
		this.fetchStates()
	},
	mounted() {
		if (this.$route?.query?.resetPassword === 'true') {
			this.forgotPassword = true
		}

		if (this.$route?.query?.createAccount === 'true') {
			this.creatingAccount = true
		}

		if (this.$route?.path === '/register') {
			this.creatingAccount = true
		}

		// Load Google Maps for address autocomplete
		loadGoogleMapsScript()
			.then(() => {
				this.googleMapsLoaded = true
				if (this.creatingAccount) {
					this.initAutoComplete()
				}
			})
			.catch((error) => {
				console.error('Error loading Google Maps script:', error)
			})
	},
	watch: {
		'user.password1.value': function (newValue) {
			this.validatePasswordComplexity(newValue)
		},
		'user.password2.value': function () {
			this.validatePasswordsMatch()
		},
		creatingAccount(newVal) {
			if (newVal && this.googleMapsLoaded) {
				this.initAutoComplete()
			}
		},
		loggedIn() {
			this.$emit('hide')
		},
		isMilitaryAddress(newVal) {
			if (newVal) {
				this.user.country.value = 'US'
				this.user.city.value = ''
				this.user.state.value = ''
				// Disable autocomplete
				if (this.autocompleteInstance) {
					google.maps.event.clearInstanceListeners(this.autocompleteInstance)
					this.autocompleteInstance = null
				}
				// Remove pac-container (autocomplete dropdown)
				const pacContainers = document.querySelectorAll('.pac-container')
				pacContainers.forEach((el) => el.remove())
				// Highlight city and state fields
				this.$nextTick(() => {
					this.applyFieldHighlight(['city', 'state'])
				})
			} else {
				// Clear military city/state values when switching back to regular address
				this.user.city.value = ''
				this.user.state.value = ''
				// Refresh the states list
				this.fetchStates()
				// Re-enable autocomplete when switching back to regular address
				if (this.googleMapsLoaded && this.creatingAccount) {
					this.initAutoComplete()
				}
				// Highlight city and state fields
				this.$nextTick(() => {
					this.applyFieldHighlight(['city', 'state'])
				})
			}
		},
	},
}
</script>

<style lang="postcss" scoped>
input,
select {
	@apply border border-gray-700 rounded px-3 py-2;
}
</style>
